/**
 * NestedContainerItem Component for UI-Model-1
 *
 * Expandable tree node for nested containers inside the "Added Components
 * or Container" config panel section. Shows a container header with
 * expand/collapse chevron, and when expanded reveals all child items
 * (fields, widgets, categories, connectors) with drag support, visibility
 * toggle, and delete support. Recursively renders deeper nested containers.
 *
 * Drag-and-drop is handled by the shared DragDropContext in
 * ContainerContentsList — this component only renders Droppable/Draggable
 * zones with a uniform type ("config-panel-item") so items can be moved
 * freely between parent and child containers.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { ContainerAPI, ItemAPI, isItemAPI } from '../../types/state.types';
import { useSelection } from '../../contexts/SelectionContext';
import { useContainerOperations } from '../../hooks/useContainerOperations';
import { getItemIcon, getItemName, getItemTypeName, truncateName } from '../../utils/itemDisplayHelpers';
import { DropdownMenu } from '../Common/DropdownMenu';
import { ConfirmationDialog } from '../Common/ConfirmationDialog';
import {
  DragIcon,
  OverFlowIcon,
  ShowEyeIcon,
  HideEyeIcon,
  DeleteIcon,
} from 'assests/GmsIcons';

// ============================================================================
// Types
// ============================================================================

interface NestedContainerItemProps {
  nestedContainer: ContainerAPI;
  level?: number;
}

interface DeleteConfirmation {
  isOpen: boolean;
  id: string;
  name: string;
  type: 'item' | 'container';
}

const INITIAL_DELETE_STATE: DeleteConfirmation = {
  isOpen: false,
  id: '',
  name: '',
  type: 'item',
};

// ============================================================================
// Inline Icons
// ============================================================================

/** Container icon — rect + line (same as old model) */
const ContainerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="2" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <line x1="1" y1="6" x2="15" y2="6" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

// ============================================================================
// Component
// ============================================================================

export const NestedContainerItem: React.FC<NestedContainerItemProps> = ({
  nestedContainer,
  level = 1,
}) => {
  const { selectItem, selectContainer } = useSelection();
  const { removeItem, updateItem, removeContainer } = useContainerOperations();

  const [isExpanded, setIsExpanded] = useState(level === 1);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmation>(INITIAL_DELETE_STATE);

  // Filter to actual ItemAPI entries
  const items: ItemAPI[] = useMemo(() => {
    return (nestedContainer.content?.items || []).filter(isItemAPI) as ItemAPI[];
  }, [nestedContainer.content?.items]);

  const childContainers = nestedContainer.content?.nestedContainers || [];
  const hasContents = items.length > 0 || childContainers.length > 0;

  // ── Visibility Toggle ────────────────────────────────────────────────────

  const handleToggleVisibility = useCallback(
    (e: React.MouseEvent, item: ItemAPI) => {
      e.stopPropagation();
      const currentVisible = item.configuration?.isVisible ?? true;
      updateItem(item.id, {
        configuration: {
          ...item.configuration,
          isVisible: !currentVisible,
        },
      });
    },
    [updateItem]
  );

  // ── Delete Flow ──────────────────────────────────────────────────────────

  const openDeleteDialog = useCallback(
    (id: string, name: string, type: 'item' | 'container') => {
      setDeleteConfirmation({ isOpen: true, id, name, type });
    },
    []
  );

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmation.type === 'item') {
      removeItem(deleteConfirmation.id);
    } else {
      removeContainer(deleteConfirmation.id);
    }
    setDeleteConfirmation(INITIAL_DELETE_STATE);
  }, [deleteConfirmation, removeItem, removeContainer]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmation(INITIAL_DELETE_STATE);
  }, []);

  // ── Click Handlers ───────────────────────────────────────────────────────

  const handleItemClick = useCallback(
    (item: ItemAPI) => {
      selectItem(item.id, item.type, nestedContainer.id);
    },
    [selectItem, nestedContainer.id]
  );

  const handleContainerClick = useCallback(() => {
    selectContainer(nestedContainer.id);
  }, [selectContainer, nestedContainer.id]);

  // ── Render ───────────────────────────────────────────────────────────────

  // Indentation: 16px per nesting level
  const indentStyle = { paddingLeft: `${level * 16}px` };

  return (
    <div>
      {/* ── Container Header Row ── */}
      <div role="button" data-testid="configpanel-nestedcontaineritem-div-1"
        className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded tw-cursor-pointer hover:tw-bg-surface-hover tw-group tw-text-ink-secondary"
        style={indentStyle}
        onClick={handleContainerClick}
      >
        {/* Expand / Collapse Chevron */}
        <button data-testid="configpanel-nestedcontaineritem-button-1" aria-label="Toggle container"
          className="tw-p-0 tw-border-none tw-bg-transparent tw-flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
        >
          <svg
            className={`tw-w-3 tw-h-3 tw-transition-transform ${isExpanded ? 'tw-rotate-90' : ''}`}
            viewBox="0 0 16 16"
          >
            <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>

        {/* Container Icon */}
        <div className="tw-flex-shrink-0">
          <ContainerIcon />
        </div>

        {/* Container Name */}
        <span
          className="tw-text-sm tw-text-ink tw-flex-1 tw-truncate"
          title={nestedContainer.name}
        >
          {truncateName(nestedContainer.name || 'Container')}
        </span>

        {/* 3-dot Menu */}
        <div className="tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
          <DropdownMenu
            trigger={<OverFlowIcon />}
            position="right"
            items={[
              {
                id: 'delete',
                label: 'Delete',
                icon: <DeleteIcon />,
                onClick: () =>
                  openDeleteDialog(
                    nestedContainer.id,
                    nestedContainer.name || 'Container',
                    'container'
                  ),
                className: 'tw-text-tone-danger-ink hover:tw-bg-tone-danger-tint',
              },
            ]}
          />
        </div>
      </div>

      {/* ── Expanded Content ── */}
      {isExpanded && (
        <div style={{ paddingLeft: `${(level + 1) * 16}px` }}>
          {/* Items list — always render Droppable so empty containers accept drops */}
            <Droppable droppableId={nestedContainer.id} type="config-panel-item">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`tw-space-y-1 tw-py-1 ${
                    snapshot.isDraggingOver ? 'tw-bg-accent-tint tw-rounded' : ''
                  }`}
                >
                  {items.map((item, index) => {
                    const name = getItemName(item);
                    const typeName = getItemTypeName(item);
                    const isVisible = item.configuration?.isVisible ?? true;

                    return (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                      >
                        {(dragProvided, dragSnapshot) => (
                          <div role="button" data-testid={`configpanel-nestedcontaineritem-div-2-${index}`}
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1 tw-rounded tw-cursor-pointer hover:tw-bg-surface-hover tw-group ${
                              dragSnapshot.isDragging
                                ? 'tw-bg-accent-tint tw-shadow-lg'
                                : ''
                            }`}
                            onClick={() => handleItemClick(item)}
                          >
                            {/* Drag Handle */}
                            <div
                              {...dragProvided.dragHandleProps}
                              className="tw-cursor-move tw-text-ink-tertiary icon-follow-ink"
                            >
                              <DragIcon />
                            </div>

                            {/* Type Icon */}
                            <div className="tw-flex-shrink-0 tw-w-4 tw-flex tw-items-center tw-justify-center tw-text-ink-secondary">
                              {getItemIcon(item)}
                            </div>

                            {/* Eye Toggle */}
                            <button data-testid={`configpanel-nestedcontaineritem-button-2-${index}`}
                              className="tw-flex-shrink-0 tw-p-1 tw-rounded hover:tw-bg-surface-hover tw-transition-colors tw-text-ink-secondary icon-follow-ink"
                              onClick={(e) => handleToggleVisibility(e, item)}
                              title={isVisible ? 'Hide item' : 'Show item'}
                            >
                              {isVisible ? <ShowEyeIcon /> : <HideEyeIcon />}
                            </button>

                            {/* Name + Type */}
                            <div className="tw-flex-1 tw-min-w-0">
                              <span
                                className="tw-text-sm tw-text-ink tw-block tw-truncate"
                                title={`${name} (${typeName})`}
                              >
                                {truncateName(name)}
                                <span className="tw-text-xs tw-text-ink-tertiary tw-ml-1">
                                  ({typeName})
                                </span>
                              </span>
                            </div>

                            {/* 3-dot Menu */}
                            <div className="tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
                              <DropdownMenu
                                trigger={<OverFlowIcon />}
                                position="right"
                                items={[
                                  {
                                    id: 'delete',
                                    label: 'Delete',
                                    icon: <DeleteIcon />,
                                    onClick: () =>
                                      openDeleteDialog(item.id, name, 'item'),
                                    className:
                                      'tw-text-tone-danger-ink hover:tw-bg-tone-danger-tint',
                                  },
                                ]}
                              />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  {items.length === 0 && (
                    <div className={`tw-py-3 tw-text-center tw-text-xs tw-rounded tw-border tw-border-dashed ${
                      snapshot.isDraggingOver
                        ? 'tw-bg-accent-tint tw-border-accent tw-text-accent'
                        : 'tw-border-edge tw-text-ink-tertiary'
                    }`}>
                      Drop items here
                    </div>
                  )}
                </div>
              )}
            </Droppable>

          {/* Recursively render deeper nested containers */}
          {childContainers.length > 0 && (
            <div className="tw-mt-1">
              {childContainers.map((deeper) => (
                <NestedContainerItem
                  key={deeper.id}
                  nestedContainer={deeper}
                  level={level + 1}
                />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!hasContents && (
            <div className="tw-text-xs tw-text-ink-tertiary tw-py-1">
              Empty container
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        title={deleteConfirmation.type === 'item' ? 'Delete Item' : 'Delete Container'}
        message={`Are you sure you want to delete "${deleteConfirmation.name}"?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default NestedContainerItem;
